import React, { useEffect, useState } from 'react';
import { compose } from 'react-apollo';
import { Helmet } from 'react-helmet';
import { SUBSCRIPTION_PREFERRED_LOCATION_CHANGED } from 'containers/DesktopNotificationTray';
import ProfileDetails from 'containers/ProfileDetailsPage/ProfileDetails';
import ProfileForm from 'containers/ProfileDetailsPage/ProfileDetails/ProfileForm';
import ProfileLoyaltyInfo from 'containers/ProfileDetailsPage/ProfileLoyaltyInfo';
import withPatronAssetsData from 'containers/ProfileDetailsPage/withPatronAssetsData';
import withPatronRewardsData from 'containers/ProfileDetailsPage/withPatronRewardsData';
import CloseButton from 'components/CloseButton';
import LoadingIndicator from 'components/LoadingIndicator';
import PageOverlay from 'components/PageOverlay';
import { getUserData } from 'utils/graphQLFunctions';
import { isLocationCmp, isLocationPls } from 'utils/locations';
import { hasPatronAccount, hasPlsAccount } from 'utils/roles';
import { ACCOUNT_TYPE } from './profileUtils';
import withPatronCmpStatusV2Data from './withPatronCmpStatusV2Data';
import withPatronPlsStatusV2Data from './withPatronPlsStatusV2Data';
import './ProfileDetailsPage.scss';

const ProfileDetailsWrapper = ({
  userData,
  statusData,
  rewardsData,
  assetsData,
  statusCmpData,
  statusPlsData,
  subscribeToLocationData
}) => {
  useEffect(() => {
    const unsubscribe = subscribeToLocationData();
    return () => unsubscribe && unsubscribe();
  }, [subscribeToLocationData]);

  const getInitialAccountType = () => {
    const user = userData?.user;
    const shouldShowPLSProfile =
      isLocationPls(user.preferredLocation) && hasPlsAccount(user);
    const shouldShowCMPProfile =
      isLocationCmp(user.preferredLocation) && hasPatronAccount(user);

    if (shouldShowPLSProfile) {
      return ACCOUNT_TYPE.PLS_ACCOUNT;
    } else if (shouldShowCMPProfile) {
      return ACCOUNT_TYPE.CMP_ACCOUNT;
    } else {
      return ACCOUNT_TYPE.LIMITED_ACCOUNT;
    }
  };

  const [activeAccountType, setActiveAccountType] = useState(
    getInitialAccountType
  );

  const handleAccountTypeChange = accountType => {
    setActiveAccountType(accountType);
  };

  const getAccountStatusData = () => {
    let data;
    switch (activeAccountType) {
      case ACCOUNT_TYPE.CMP_ACCOUNT:
        data = statusCmpData;
        break;
      case ACCOUNT_TYPE.PLS_ACCOUNT:
        data = statusPlsData;
        break;
      default:
        data = statusPlsData;
    }
    return data;
  };

  return (
    <div className="profile-details-wrapper">
      <ProfileLoyaltyInfo
        userData={userData}
        statusData={statusData}
        cmpStatusData={statusCmpData}
        plsStatusData={statusPlsData}
        assetsData={assetsData}
        activeAccountType={activeAccountType}
        onAccountTypeChange={handleAccountTypeChange}
      />
      <ProfileDetails
        formComponent={ProfileForm}
        userData={userData}
        accountStatusData={getAccountStatusData()}
        rewardsData={rewardsData}
        editable={true}
        activeAccountType={activeAccountType}
        onAccountTypeChange={handleAccountTypeChange}
      />
    </div>
  );
};

const ProfileDetailsWithPlsAndCmpData = compose(
  withPatronCmpStatusV2Data,
  withPatronPlsStatusV2Data,
  withPatronAssetsData,
  withPatronRewardsData
)(ProfileDetailsWrapper);

const ProfileDetailsWithCmpData = compose(
  withPatronCmpStatusV2Data,
  withPatronAssetsData,
  withPatronRewardsData
)(ProfileDetailsWrapper);

const ProfileDetailsWithPLSData = compose(
  withPatronPlsStatusV2Data,
  withPatronAssetsData,
  withPatronRewardsData
)(ProfileDetailsWrapper);

const ProfileDetailsWithLimitedData = compose(withPatronAssetsData)(
  ProfileDetailsWrapper
);

function ProfileDetailsPage({ userData, subscribeToLocationData }) {
  const user = userData.user;
  if (userData.loading && !user) {
    return <LoadingIndicator expand />;
  } else if (userData.error || !user) {
    return (
      <div className="alert alert-danger">
        Failed to retrieve player info. If this continues, please contact
        support.
      </div>
    );
  }

  const ProfileDetailsComponent =
    hasPatronAccount(user) && hasPlsAccount(user)
      ? ProfileDetailsWithPlsAndCmpData
      : hasPatronAccount(user)
      ? ProfileDetailsWithCmpData
      : hasPlsAccount(user)
      ? ProfileDetailsWithPLSData
      : ProfileDetailsWithLimitedData;

  return (
    <div className="profile-details-page">
      <PageOverlay>
        <Helmet>
          <title>My Profile</title>
        </Helmet>
        <div className="profile-details-page-title-container">
          <h1 className="profile-details-page-title">My Profile</h1>
        </div>
        <ProfileDetailsComponent
          userData={userData}
          subscribeToLocationData={subscribeToLocationData}
        />
        <CloseButton url="/" />
      </PageOverlay>
    </div>
  );
}

export default getUserData({
  name: 'userData',
  props: ({ userData }) => {
    return {
      userData,
      subscribeToLocationData: () => {
        return userData.subscribeToMore({
          document: SUBSCRIPTION_PREFERRED_LOCATION_CHANGED,
          updateQuery: (prev, { subscriptionData }) => {
            if (
              !!subscriptionData?.data?.newPreferredLocation &&
              subscriptionData.data.newPreferredLocation !==
                prev?.user?.preferredLocation
            ) {
              prev.user.preferredLocation =
                subscriptionData.data.newPreferredLocation;
            }
            return prev;
          }
        });
      }
    };
  }
})(ProfileDetailsPage);
